import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { PdfRichDocumentEditor } from '../components/PdfRichDocumentEditor.jsx';
import { downloadPdfDocument } from '../components/PdfDocumentsPanel.jsx';
import { Modal } from '../components/Modal.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { BLANK_DOCUMENT_HTML, DEFAULT_PAGE_MARGINS_MM, htmlDocumentToPdfBlob, normalizePageMargins, reportToHtml } from '../utils/htmlToPdf.js';
import { compressHtmlDataImages } from '../utils/compressHtmlImages.js';
import { runPdfDocumentImport } from '../utils/runPdfDocumentImport.js';
import { uploadPdfCompiled } from '../api/pdfDocuments.js';
import { clientLabel } from '../utils/clientLabel.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 2 : 1)} MB`;
}

export function AdminPdfDocumentEditorPage() {
  const { id: routeId } = useParams();
  const documentId = Number(routeId);
  const hasValidId = Number.isFinite(documentId) && documentId > 0;
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [doc, setDoc] = useState(null);
  const [html, setHtml] = useState(BLANK_DOCUMENT_HTML);
  const [previewUrl, setPreviewUrl] = useState('');
  const [view, setView] = useState('edit'); // edit | preview
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [visibility, setVisibility] = useState('draft');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorMeta, setEditorMeta] = useState({});
  const [pageMargins, setPageMargins] = useState(() => normalizePageMargins(DEFAULT_PAGE_MARGINS_MM));

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPurpose, setAiPurpose] = useState('edit');
  const [aiTone, setAiTone] = useState('professional');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiFormat, setAiFormat] = useState('text');

  const [showShare, setShowShare] = useState(false);
  const [shareAudience, setShareAudience] = useState('client');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);

  const projectOptions = useMemo(() => {
    if (!clientId) return projects;
    return projects.filter((p) => String(p.client_id) === String(clientId));
  }, [projects, clientId]);

  const taskOptions = useMemo(() => {
    if (!projectId) return tasks;
    return tasks.filter((t) => String(t.project_id) === String(projectId));
  }, [tasks, projectId]);

  const draftSize = useMemo(() => {
    const bytes = new TextEncoder().encode(html || '').length;
    const images = (html || '').match(/<img\b/gi)?.length ?? 0;
    return { bytes, images };
  }, [html]);

  const savedPdfBytes = Number(doc?.compiled_size || doc?.file_size || 0);

  const resolveHtmlFromState = (detail) => {
    const state = detail.editor_state || {};
    if (typeof state.html === 'string' && state.html.trim()) {
      return state.html;
    }
    if (state.report && typeof state.report === 'object') {
      return reportToHtml(state.report, {
        url: state.analyzed_url,
        pagesScanned: state.pages_scanned,
      });
    }
    return `<h1>${detail.title || 'Document'}</h1><p>${detail.description || 'Start editing this document.'}</p>`;
  };

  const load = async () => {
    if (!hasValidId) {
      setLoading(false);
      setError('Invalid document link. Open the document again from the PDF documents list.');
      return;
    }
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const [detail, clientList, projectList, taskList] = await Promise.all([
        apiJson(`/api/admin/pdf-documents/${documentId}`),
        apiJson('/api/admin/clients'),
        apiJson('/api/admin/projects'),
        apiJson('/api/admin/tasks'),
      ]);
      let current = detail;
      let nextHtml = resolveHtmlFromState(detail);
      const state = detail.editor_state || {};

      // Only finish a first-upload import that was interrupted — never re-AI an already saved document.
      if (state.needs_text_import === true && !state.report) {
        setStatus('Finishing first-time PDF import (saved once, then edited as-is)…');
        try {
          current = await runPdfDocumentImport(documentId, {
            title: detail.title || '',
            onStatus: setStatus,
          });
          nextHtml = resolveHtmlFromState(current);
          setStatus('Imported and saved. Further edits use this document only.');
        } catch (importErr) {
          setStatus('');
          setError(importErr?.message || 'Could not finish the first-time PDF import.');
        }
      }

      setDoc(current);
      setTitle(current.title || '');
      setDescription(current.description || '');
      setClientId(current.client_id ? String(current.client_id) : '');
      setProjectId(current.project_id ? String(current.project_id) : '');
      setTaskId(current.task_id ? String(current.task_id) : '');
      setVisibility(current.visibility || 'draft');
      const nextState = current.editor_state || {};
      setEditorMeta({
        source: nextState.source,
        analyzed_url: nextState.analyzed_url,
        pages_scanned: nextState.pages_scanned,
        report: nextState.report,
        imported_from_pdf: nextState.imported_from_pdf,
        imported_layout_version: nextState.imported_layout_version,
        imported_pages: nextState.imported_pages,
        imported_has_text: nextState.imported_has_text,
        needs_text_import: false,
        import_source: nextState.import_source,
      });
      setPageMargins(normalizePageMargins(nextState.page_margins));
      setHtml(nextHtml);
      setClients(clientList);
      setProjects(projectList);
      setTasks(taskList);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [documentId, hasValidId]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const buildEditorState = (nextHtml = html) => ({
    ...editorMeta,
    version: 2,
    mode: 'document',
    html: nextHtml,
    needs_text_import: false,
    page_margins: normalizePageMargins(pageMargins),
  });

  const save = async ({ compile = true, openPreview = false } = {}) => {
    if (!hasValidId) {
      setError('Invalid document link. Open the document again from the PDF documents list.');
      return;
    }
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const compactHtml = await compressHtmlDataImages(html, { margins: pageMargins });
      if (compactHtml !== html) {
        setHtml(compactHtml);
      }
      const updated = await apiJson(`/api/admin/pdf-documents/${documentId}`, {
        method: 'PUT',
        body: {
          title,
          description,
          client_id: clientId ? Number(clientId) : null,
          project_id: projectId ? Number(projectId) : null,
          task_id: taskId ? Number(taskId) : null,
          visibility,
          editor_state: buildEditorState(compactHtml),
        },
      });
      setDoc(updated);
      const state = updated.editor_state || {};
      setEditorMeta({
        source: state.source,
        analyzed_url: state.analyzed_url,
        pages_scanned: state.pages_scanned,
        report: state.report,
        imported_from_pdf: state.imported_from_pdf,
        imported_layout_version: state.imported_layout_version,
        imported_pages: state.imported_pages,
        imported_has_text: state.imported_has_text,
        needs_text_import: false,
        import_source: state.import_source,
      });

      if (compile) {
        // Write edited PDF to compiled_path only — never overwrite the original uploaded source.
        const pdfBlob = await htmlDocumentToPdfBlob(compactHtml, {
          title,
          margins: normalizePageMargins(pageMargins),
        });
        const form = new FormData();
        form.append('file', pdfBlob, updated.file_name || 'document.pdf');
        const replaced = await uploadPdfCompiled(documentId, form);
        setDoc(replaced);

        if (openPreview) {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          const url = URL.createObjectURL(pdfBlob);
          setPreviewUrl(url);
          setView('preview');
        }
      }

      setStatus(compile ? 'Document saved. Original upload preserved; download uses the edited PDF.' : 'Document saved.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runAi = async () => {
    if (!aiPrompt.trim()) {
      setError('Enter a prompt for AI generation.');
      return;
    }
    setAiBusy(true);
    setError('');
    setStatus('');
    try {
      const compactHtml = await compressHtmlDataImages(html, { margins: pageMargins });
      if (compactHtml !== html) {
        setHtml(compactHtml);
      }
      const result = await apiJson('/api/admin/pdf-documents/ai-generate', {
        method: 'POST',
        body: {
          prompt: aiPrompt,
          purpose: aiPurpose,
          tone: aiTone,
          document_html: compactHtml,
          context: [
            title ? `Document title: ${title}` : '',
            description ? `Current description: ${description}` : '',
          ].filter(Boolean).join('\n'),
        },
      });
      let nextText = result.text || '';
      const nextFormat = result.format === 'html' || aiPurpose === 'edit' ? 'html' : 'text';
      if (nextFormat === 'html' && nextText.trim()) {
        nextText = await compressHtmlDataImages(nextText, { margins: pageMargins });
      }
      setAiText(nextText);
      setAiFormat(nextFormat);

      if (aiPurpose === 'edit' && nextText.trim()) {
        setHtml(nextText);
        setStatus('Document updated from OpenAI. Save to write the edited PDF.');
        setAiText('');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAiBusy(false);
    }
  };

  const applyAi = async () => {
    if (!aiText.trim()) return;
    if (aiPurpose === 'edit' || aiFormat === 'html') {
      const next = await compressHtmlDataImages(aiText.trim(), { margins: pageMargins });
      setHtml(next);
      setStatus('Document updated from OpenAI. Save to write the edited PDF.');
      setAiText('');
      return;
    }
    if (aiPurpose === 'title') {
      setTitle(aiText.trim());
      return;
    }
    if (aiPurpose === 'description') {
      setDescription(aiText.trim());
      return;
    }
    const block = aiPurpose === 'section'
      ? `<h2>Section</h2><p>${aiText.trim().replace(/\n/g, '<br>')}</p>`
      : `<p>${aiText.trim().replace(/\n/g, '<br>')}</p>`;
    setHtml((prev) => `${prev || ''}\n${block}`);
  };

  const share = async (e) => {
    e.preventDefault();
    if (!hasValidId) {
      setError('Invalid document link. Open the document again from the PDF documents list.');
      return;
    }
    setSharing(true);
    setError('');
    try {
      await save({ compile: true });
      const updated = await apiJson(`/api/admin/pdf-documents/${documentId}/share`, {
        method: 'POST',
        body: {
          audience: shareAudience,
          message: shareMessage || null,
        },
      });
      setDoc(updated);
      setVisibility(updated.visibility);
      setShowShare(false);
      setStatus(shareAudience === 'client' ? 'Shared with the client portal.' : 'Shared with the team.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSharing(false);
    }
  };

  const previewDownload = async () => {
    if (!hasValidId) {
      setError('Invalid document link. Open the document again from the PDF documents list.');
      return;
    }
    try {
      await save({ compile: true });
      const blob = await downloadPdfDocument(documentId, 'admin');
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    document.documentElement.classList.add('pdf-editor-page-active');
    document.body.classList.add('pdf-editor-page-active');

    const syncInset = () => {
      const header = document.querySelector('.dashboard-header');
      const top = header instanceof HTMLElement
        ? Math.ceil(header.getBoundingClientRect().bottom)
        : 0;
      document.documentElement.style.setProperty('--pdf-editor-inset-top', `${top}px`);
    };
    syncInset();
    window.addEventListener('resize', syncInset);
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(syncInset)
      : null;
    const header = document.querySelector('.dashboard-header');
    if (ro && header) ro.observe(header);

    return () => {
      document.documentElement.classList.remove('pdf-editor-page-active');
      document.body.classList.remove('pdf-editor-page-active');
      document.documentElement.style.removeProperty('--pdf-editor-inset-top');
      window.removeEventListener('resize', syncInset);
      ro?.disconnect();
    };
  }, []);

  if (loading) return <p className="muted">Loading editor…</p>;
  if (!doc && error) return <p className="err">{error}</p>;

  return (
    <div className="pdf-editor-layout">
      <div className="page-header-row">
        <div>
          <p className="muted small" style={{ marginBottom: 4 }}>
            <Link to="/admin/pdf-documents">← PDF documents</Link>
          </p>
          <h2 style={{ marginTop: 0 }}>{title || 'Document editor'}</h2>
          <p className="muted small pdf-editor-size-line" title="Draft is the editor HTML payload sent on Save. PDF is the last compiled download file.">
            Draft {formatBytes(draftSize.bytes)}
            {draftSize.images > 0 ? ` · ${draftSize.images} image${draftSize.images === 1 ? '' : 's'}` : ''}
            {savedPdfBytes > 0 ? ` · PDF ${formatBytes(savedPdfBytes)}` : ''}
          </p>
        </div>
        <div className="pdf-editor-actions">
          <button type="button" className={`btn ${view === 'edit' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setView('edit')}>
            Edit
          </button>
          <button
            type="button"
            className={`btn ${view === 'preview' ? 'btn-primary' : 'btn-outline-secondary'}`}
            disabled={saving}
            onClick={() => void save({ compile: true, openPreview: true })}
          >
            PDF preview
          </button>
          <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => void previewDownload()}>
            Download PDF
          </button>
          <button type="button" className="btn btn-outline-primary" disabled={saving} onClick={() => void save({ compile: true })}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowShare(true)}>
            Share
          </button>
        </div>
      </div>

      {error ? <p className="err">{error}</p> : null}
      {status ? <p className="ok">{status}</p> : null}

      <div className="pdf-editor-grid">
        <div className="pdf-editor-main auth-card flat">
          {view === 'edit' ? (
            <PdfRichDocumentEditor
              value={html}
              onChange={setHtml}
              disabled={saving}
              title={title || 'Document'}
              margins={pageMargins}
            />
          ) : previewUrl ? (
            <iframe title="PDF preview" className="pdf-preview-frame" src={previewUrl} />
          ) : (
            <p className="muted">Generate a preview with the PDF preview button.</p>
          )}
        </div>

        <aside className="pdf-editor-sidebar stack">
          <div className="auth-card flat stack">
            <strong>Document</strong>
            <div className="pdf-editor-size-box" title="Draft updates as you edit. PDF size updates after Save.">
              <div className="pdf-editor-size-row">
                <span>Draft size</span>
                <strong>{formatBytes(draftSize.bytes)}</strong>
              </div>
              <div className="pdf-editor-size-row">
                <span>Images</span>
                <strong>{draftSize.images}</strong>
              </div>
              <div className="pdf-editor-size-row">
                <span>Saved PDF</span>
                <strong>{savedPdfBytes > 0 ? formatBytes(savedPdfBytes) : 'Not saved yet'}</strong>
              </div>
              {draftSize.bytes >= 8 * 1024 * 1024 ? (
                <p className="muted small" style={{ margin: 0 }}>
                  Large draft — images are compressed to PDF page size on Save.
                </p>
              ) : null}
            </div>
            <label className="field">
              <span>Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="field">
              <span>Description</span>
              <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <div className="field">
              <span>Page margins (mm)</span>
              <div className="pdf-margin-grid">
                {(['top', 'right', 'bottom', 'left']).map((side) => (
                  <label key={side} className="pdf-margin-field">
                    <span>{side[0].toUpperCase() + side.slice(1)}</span>
                    <input
                      type="number"
                      min={0}
                      max={80}
                      step={1}
                      value={pageMargins[side]}
                      onChange={(e) => {
                        const next = Number(e.target.value);
                        setPageMargins((prev) => normalizePageMargins({
                          ...prev,
                          [side]: Number.isFinite(next) ? next : prev[side],
                        }));
                      }}
                    />
                  </label>
                ))}
              </div>
              <label className="pdf-margin-all">
                <span>All sides</span>
                <input
                  type="number"
                  min={0}
                  max={80}
                  step={1}
                  value={
                    pageMargins.top === pageMargins.right
                    && pageMargins.top === pageMargins.bottom
                    && pageMargins.top === pageMargins.left
                      ? pageMargins.top
                      : ''
                  }
                  placeholder="—"
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    if (!Number.isFinite(next)) return;
                    setPageMargins(normalizePageMargins({
                      top: next,
                      right: next,
                      bottom: next,
                      left: next,
                    }));
                  }}
                />
              </label>
              <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
                Applied in the editor and when generating the PDF.
              </p>
            </div>
            <label className="field">
              <span>Client</span>
              <SearchableSelect
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  setProjectId('');
                  setTaskId('');
                }}
              >
                <option value="">None</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{clientLabel(c)}</option>
                ))}
              </SearchableSelect>
            </label>
            <label className="field">
              <span>Project</span>
              <SearchableSelect
                value={projectId}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setTaskId('');
                }}
              >
                <option value="">None</option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </SearchableSelect>
            </label>
            <label className="field">
              <span>Task</span>
              <SearchableSelect value={taskId} onChange={(e) => setTaskId(e.target.value)}>
                <option value="">None</option>
                {taskOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </SearchableSelect>
            </label>
            <label className="field">
              <span>Visibility</span>
              <SearchableSelect value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                <option value="draft">Draft</option>
                <option value="team">Team</option>
                <option value="client">Client</option>
              </SearchableSelect>
            </label>
          </div>

          <div className="auth-card flat stack">
            <strong>OpenAI assistant</strong>
            {!caps.openai ? (
              <p className="muted small">OpenAI plan feature is required. Configure the integration under Integrations.</p>
            ) : (
              <>
                <p className="muted small">
                  Sends the current document (text + charts/images) with your prompt. New charts are generated and embedded. Save to write the PDF.
                </p>
                <label className="field">
                  <span>Purpose</span>
                  <SearchableSelect value={aiPurpose} onChange={(e) => setAiPurpose(e.target.value)}>
                    <option value="edit">Edit document</option>
                    <option value="section">Insert section</option>
                    <option value="body">Insert paragraph</option>
                    <option value="description">Description</option>
                    <option value="title">Title</option>
                  </SearchableSelect>
                </label>
                <label className="field">
                  <span>Tone</span>
                  <SearchableSelect value={aiTone} onChange={(e) => setAiTone(e.target.value)}>
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="concise">Concise</option>
                    <option value="persuasive">Persuasive</option>
                  </SearchableSelect>
                </label>
                <label className="field">
                  <span>Prompt</span>
                  <textarea
                    rows={4}
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={
                      aiPurpose === 'edit'
                        ? 'e.g. Shorten the intro and expand the SEO recommendations with concrete examples'
                        : 'e.g. Expand the SEO recommendations with concrete examples'
                    }
                  />
                </label>
                <button type="button" className="btn btn-outline-primary" disabled={aiBusy} onClick={() => void runAi()}>
                  {aiBusy
                    ? (aiPurpose === 'edit' ? 'Editing…' : 'Generating…')
                    : (aiPurpose === 'edit' ? 'Edit with OpenAI' : 'Generate with OpenAI')}
                </button>
                {aiText ? (
                  <>
                    <label className="field">
                      <span>{aiFormat === 'html' ? 'Revised HTML' : 'Generated text'}</span>
                      <textarea rows={5} value={aiText} onChange={(e) => setAiText(e.target.value)} />
                    </label>
                    <button type="button" className="btn btn-primary" onClick={() => void applyAi()}>
                      {aiFormat === 'html' || aiPurpose === 'edit' ? 'Replace document' : 'Apply'}
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>

          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/admin/pdf-documents')}>
            Back to list
          </button>
        </aside>
      </div>

      <Modal open={showShare} title="Share PDF" onClose={() => !sharing && setShowShare(false)}>
        <form className="stack" onSubmit={share}>
          <p className="muted small">Saves the document, generates the PDF, then notifies recipients.</p>
          <label className="field">
            <span>Audience</span>
            <SearchableSelect value={shareAudience} onChange={(e) => setShareAudience(e.target.value)}>
              <option value="client">Client portal users</option>
              <option value="team">Agency team</option>
            </SearchableSelect>
          </label>
          {shareAudience === 'client' && !clientId ? (
            <p className="err">Attach a client before sharing to the client portal.</p>
          ) : null}
          <label className="field">
            <span>Message (optional)</span>
            <textarea rows={3} value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} />
          </label>
          <div className="modal-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={sharing} onClick={() => setShowShare(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={sharing || (shareAudience === 'client' && !clientId)}
            >
              {sharing ? 'Sharing…' : 'Share now'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
